/* eslint-disable jsx-a11y/anchor-is-valid */
import NextLink from 'next/link'
import React from 'react'
import { IoArrowForwardSharp } from 'react-icons/io5'
import styled from 'styled-components'
import { UserRole } from 'types/graphql'
import { PencilIcon } from 'icons/Pencil'
import { media } from 'utils/media'
import { LinkButton } from 'lib/Button/Button'
import { ShowForDesktop, ShowForMobile } from 'lib/ShowComponent'
import { StarRating } from 'lib/StarRating'
import { Text } from 'lib/Text'
import { Action } from 'modules/auth/abilities'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { ListActions } from 'modules/member/lists/components/BrandListActions'
import { BrandWebUrl } from '../common/BrandWebUrl'
import { useCurrentBrand } from './context/CurrentBrandProvider'

const InfoContent = styled.header`
  margin-top: 40px;
  ${media.tabletDown} {
    margin-top: 0;
    padding: var(--grid-gap-small);
    z-index: 2;
    position: relative;
  }
`

const Description = styled(Text)`
  color: var(--neutral-60);
  margin-top: 8px;
  max-width: 60%;
  ${media.tabletDown} {
    max-width: 85%;
  }
  ${media.phoneDown} {
    max-width: 100%;
  }
`

const EditButton = styled(LinkButton)`
  margin-left: 12px;
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`

const EditAnchor = styled.a`
  display: flex;
  margin-left: 12px;

  ${media.phoneUp} {
    display: none;
  }
`

const EditIcon = styled(PencilIcon)`
  width: 24px;
  height: 24px;

  ${media.phoneDown} {
    width: 32px;
    height: 32px;
    margin-left: 0;
  }
`

const ActionsContainer = styled.div`
  display: flex;
  margin: 16px 0;

  ${media.phoneDown} {
    flex-direction: column;
  }
`

const ListActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const RatingWrapper = styled.a`
  align-items: center;
  display: inline-flex;
  margin-bottom: 8px;
  min-height: 32px;
  text-decoration: none;
  white-space: nowrap;
`

const TextRating = styled(Text)`
  margin-left: 16px;
  line-height: 16px;
`

const RipBrandUrl = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

export const Info = () => {
  const { user, ability } = useCurrentUser()
  const { brand } = useCurrentBrand()

  const isAdmin = user?.role === UserRole.Admin
  const isContributorOfBrand = ability.can(Action.Create, { __typename: 'BrandEditSuggestion', brandId: +brand.id })
  /**
   * Adding the `key` below fixes the bug where a brand image is not immediately being replaced when
   * navigating to a new page
   */
  return (
    <React.Fragment key={brand.id}>
      <InfoContent>
        <TitleWrapper>
          <Text as="h1" weight="bold" size="xl">
            {brand.title}
          </Text>

          <ShowForMobile>
            {isAdmin && (
              <NextLink legacyBehavior href={`/admin/brands/${brand.id}`} passHref>
                <EditAnchor aria-label="Edit">
                  <EditIcon />
                </EditAnchor>
              </NextLink>
            )}

            {!isAdmin && isContributorOfBrand && (
              <NextLink legacyBehavior href={`/brandmin/${brand.slug}`} passHref>
                <EditAnchor>
                  <EditIcon />
                </EditAnchor>
              </NextLink>
            )}
          </ShowForMobile>
        </TitleWrapper>

        {brand.reviewSummary && (
          <RatingWrapper href="#review-marker">
            <StarRating stars={brand.reviewSummary.averageRating || 0} />
            <TextRating size="pxs" weight="regular" color="neutral-80">
              {brand.reviewSummary.averageRating} • {brand.reviewSummary.ratingsCount} {brand.reviewSummary.ratingsCount === 1 ? 'Review' : 'Reviews'}
            </TextRating>
          </RatingWrapper>
        )}

        {brand.description && (
          <Description weight="regular" size="ps" margin="2x">
            {brand.description}
          </Description>
        )}

        {brand.rip ? (
          <RipBrandUrl>
            <IoArrowForwardSharp size={24} color="#000" />
            <Text weight="medium" size="p">
              {brand.webUrl}
            </Text>
          </RipBrandUrl>
        ) : (
          <>
            <ActionsContainer>
              <ListActionsContainer>
                <ListActions brandId={brand.id} brandTitle={brand.title} brandSlug={brand.slug} reviewId={brand.myReview?.id} />
                <ShowForDesktop>
                  {isAdmin && (
                    <EditButton href={`/admin/brands/${brand.id}`}>
                      <EditIcon />
                    </EditButton>
                  )}

                  {!isAdmin && isContributorOfBrand && (
                    <EditButton href={`/brandmin/${brand.slug}`}>
                      <EditIcon />
                    </EditButton>
                  )}
                </ShowForDesktop>
              </ListActionsContainer>
            </ActionsContainer>

            <BrandWebUrl brandId={brand.id} label={brand.webUrl || ''} />
          </>
        )}
      </InfoContent>
    </React.Fragment>
  )
}
